import Script from 'next/script'
import * as React from 'react'
import { useFeatureValueLoadable } from 'growthbook'
import { useUserLocation } from 'hooks/use-user-location'

import {
  createIubendaBridgeScript,
  iubendaConsentEventName,
  IUBENDA_FEATURE_FLAG,
  IubendaStyles,
  iubendaSrc,
  isIubendaBridgeReady,
  setIubendaGdprApplies,
  setIubendaUsPrivacyApplies,
  useIubenda,
} from '@repo/analytics/iubenda'
import type { IubendaConsentEventDetail } from '@repo/analytics/iubenda'

const IubendaGdprContext = React.createContext(false)

export function IubendaGdprProvider({ children }: React.PropsWithChildren) {
  const { gdprApplies, usprApplies, variant } = useResolvedIubendaConfig(true)

  React.useInsertionEffect(() => {
    setIubendaGdprApplies(variant === 'locationNotSet' || Boolean(gdprApplies))
    setIubendaUsPrivacyApplies(Boolean(usprApplies), variant === 'strictMode')
  }, [gdprApplies, usprApplies, variant])

  React.useEffect(() => {
    // Countries without a widget still need to wake mounted consent subscribers.
    window.dispatchEvent(
      new CustomEvent(iubendaConsentEventName, {
        detail: { type: 'location-resolved' },
      }),
    )
  }, [gdprApplies, usprApplies, variant])

  return (
    <IubendaGdprContext value>
      {children}
      <IubendaRuntime />
    </IubendaGdprContext>
  )
}

const isBrowserTest = () => process.env.TEST_BROWSER === '1'

export type IubendaConfig = ReturnType<typeof useIubendaConfig>

export const useIubendaConfig = () => {
  return useResolvedIubendaConfig(React.useContext(IubendaGdprContext))
}

function useResolvedIubendaConfig(enableGdpr: boolean) {
  const iubendaFeature = useFeatureValueLoadable<boolean>(IUBENDA_FEATURE_FLAG)
  const userLocation = useUserLocation()
  const isLoading = !isBrowserTest() && iubendaFeature.isLoading
  const enabled = !isBrowserTest() && !isLoading && iubendaFeature.data === true
  const iubendaConfig = useIubenda({
    country: userLocation.country,
    enableGdpr: enableGdpr && enabled,
    enabled,
    locationSet: userLocation.locationSet,
    region: userLocation.region,
  })

  if (isLoading) {
    return {
      ...iubendaConfig,
      scriptProps: {},
      variant: 'locationNotSet' as const,
    }
  }

  return iubendaConfig
}

function IubendaRuntime() {
  const { variant, gdprApplies, usprApplies } = useIubendaConfig()
  const [bridgeReadyVariant, setBridgeReadyVariant] = React.useState<string | null>(null)

  const markBridgeReady = React.useCallback(() => {
    setBridgeReadyVariant(variant)
  }, [variant])

  React.useEffect(() => {
    const handleConsentEvent = (event: Event) => {
      const detail = (event as CustomEvent<Partial<IubendaConsentEventDetail>>).detail

      if (detail?.type === 'bridge-ready') {
        markBridgeReady()
      }
    }

    window.addEventListener(iubendaConsentEventName, handleConsentEvent)

    if (isIubendaBridgeReady()) {
      markBridgeReady()
    }

    return () => {
      window.removeEventListener(iubendaConsentEventName, handleConsentEvent)
    }
  }, [markBridgeReady])

  const bridgeReady = bridgeReadyVariant === variant
  const autoOpenPreferences = variant === 'strictMode' && !gdprApplies
  const shouldRender = variant !== 'locationNotSet' && variant !== 'notApplicable'

  return shouldRender ? (
    <>
      <IubendaStyles />
      <Script
        id="iubenda-react-bridge-script"
        strategy="afterInteractive"
        onReady={markBridgeReady}
      >
        {createIubendaBridgeScript({ autoOpenPreferences, gdprApplies, usprApplies })}
      </Script>
      {bridgeReady ? (
        <Script
          type="text/javascript"
          id="iubenda-script"
          strategy="afterInteractive"
          src={iubendaSrc}
        />
      ) : null}
    </>
  ) : null
}

export const IubendaScripts = () => {
  const hasGdprRuntime = React.useContext(IubendaGdprContext)

  return hasGdprRuntime ? null : <IubendaRuntime />
}
